import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { Deal, TradeIn } from '@upstart/harmony/lib/legos/dealTypes';

import './TradePayoffModal.scss';
import { LossExpressSearchRequest } from '@upstart/harmony/lib/legos/lossExpressTypes';
import { Button } from 'react-bootstrap';
import TradePayoffForm from './TradePayoffForm';
import { scrollableModalStyles } from '../../../util/constants';
import TradePayoffLoading from './TradePayoffLoading';
import { PriceInput } from '../../PencilEditor/Input';
import LossExpressService from '../../../services/LossExpressService';
import DealService from '../../../services/DealService';
import { TRADE_PAYOFF_MODAL_TEXT } from './constants';
import { TradePayoffResults } from './TradePayoffResults';
import { TradePayoffState } from '../../../util/tradePayoffUtils';

interface TradePayoffModalProps {
  dealer: Dealer;
  deal: Deal;
  tradeIn?: TradeIn;
  tradeInIndex?: number;
  tradePayoffState: TradePayoffState;
  isOpen: boolean;
  onClose: () => void;
  updatePayoffAmount?: (amount: number) => void;
  successButtonText?: string;
}

const styles = {
  ...scrollableModalStyles,
  content: {
    ...scrollableModalStyles.content,
    maxWidth: '50vw',
    maxHeight: '600vh',
  },
};

const TradePayoffModal = (props: TradePayoffModalProps) => {
  const [formSubmitted, setIsFormSubmitted] = useState(false);
  const [formSubmitting, setIsFormSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [showPayoffResults, setShowPayoffResults] = useState(false);
  const [estimatedPayoff, setEstimatedPayoff] = useState(0);
  const [showPayoffTimeout, setShowPayoffTimeout] = useState(false);

  useEffect(() => {
    // set state to default form - if we receive error (Not Found), only do this when modal is closed
    if (
      (props.tradePayoffState === TradePayoffState.NOT_FOUND &&
        !props.isOpen) ||
      props.tradePayoffState === TradePayoffState.DEFAULT
    ) {
      setShowPayoffResults(false);
      setShowPayoffTimeout(false);
      setIsFormSubmitted(false);
      setIsFormSubmitting(false);
      setEstimatedPayoff(0);
      setSubmissionError('');
    }

    if (
      props.tradePayoffState === TradePayoffState.VALID ||
      props.tradePayoffState === TradePayoffState.EXPIRED
    ) {
      // we've received a successful result from Loss Express (even if expired), show results
      setShowPayoffResults(true);
    }
  }, [props.tradePayoffState]);

  useEffect(() => {
    let timer: NodeJS.Timer;
    // payoff request has been submitted but haven't received response after 10 seconds, allow user to enter estimated
    // amount
    if (formSubmitted) {
      timer = setTimeout(() => setShowPayoffTimeout(true), 10000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [formSubmitted]);

  const updateTradeInVIN = async (vin: string) => {
    // update existing trade in to use new vin
    const updatedDealTradeIns = props.deal.tradeIns?.map((tradeIn, index) => {
      if (index === props.tradeInIndex) {
        return {
          ...tradeIn,
          id: vin,
        };
      }
      return tradeIn;
    });

    await DealService.updateDeal({
      dealId: props.deal.id,
      body: {
        tradeIns: updatedDealTradeIns,
      },
    });
  };

  const onSubmit = async (payoffRequest: LossExpressSearchRequest) => {
    setSubmissionError('');
    setIsFormSubmitting(true);

    // update trade in VIN to value in form if current trade in doesn't have a VIN specified
    if (!props.tradeIn?.id) {
      await updateTradeInVIN(payoffRequest.vin);
    }

    LossExpressService.lookupPayoff(props.deal.id, payoffRequest)
      .then(() => {
        setIsFormSubmitted(true);
      })
      .catch(ex => {
        setSubmissionError(
          'An error occurred while attempting to lookup trade payoff',
        );
      })
      .finally(() => {
        setIsFormSubmitting(false);
      });
  };

  const handlePayoffAmountChange = () => {
    if (props.updatePayoffAmount) {
      props.updatePayoffAmount(estimatedPayoff);
    }
    props.onClose();
  };

  const isLoading =
    formSubmitting ||
    (formSubmitted &&
      props.tradePayoffState !== TradePayoffState.VALID &&
      props.tradePayoffState !== TradePayoffState.EXPIRED);

  const getLookupPendingContent = () => (
    <div className="trade-payoff-content">
      <div>
        <h3>We&#39;re still searching for the Trade Payoff</h3>
      </div>
      <div>It may take up to 24 hours to confirm the Trade Payoff amount.</div>
      <div>
        While you wait, you may continue the deal by estimating the Trade Payoff
      </div>
      <div>
        <span className="b">Note: </span>{' '}
        {TRADE_PAYOFF_MODAL_TEXT.ESTIMATED_AMOUNT}
      </div>
      <div>
        <span className="b">Estimated Trade Payoff (Optional)</span>
      </div>
      <div>
        <PriceInput
          value={estimatedPayoff}
          onChange={value => setEstimatedPayoff(value)}
          displayDollarSign
        />
      </div>
      <div className="text-center">
        <Button onClick={handlePayoffAmountChange} className="btn modal-button">
          Apply and Save
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={props.isOpen}
      contentLabel="Trade Payoff"
      style={styles as any}
      ariaHideApp={false}
    >
      <span className="close" onClick={props.onClose}>
        X
      </span>
      {(showPayoffResults && (
        <TradePayoffResults
          successButtonText={props.successButtonText}
          tradeIn={props.tradeIn}
          onClose={props.onClose}
        />
      )) ||
        (showPayoffTimeout && getLookupPendingContent()) ||
        ((formSubmitting || formSubmitted) && (
          <TradePayoffLoading
            payoffResponseLoading={isLoading}
            onClose={props.onClose}
          />
        )) || (
          <>
            <TradePayoffForm
              dealer={props.dealer}
              deal={props.deal}
              tradeIn={props.tradeIn}
              onSubmit={onSubmit}
              onClose={props.onClose}
            />
            {submissionError && (
              <div className="error-container">{submissionError}</div>
            )}
          </>
        )}
    </Modal>
  );
};

export default TradePayoffModal;
